import { useState, useEffect } from "react"
import { 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  TextField, 
  Box, 
  Alert, 
  CircularProgress,
  Grid,
  Divider
} from "@mui/material"
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useTranslation } from 'react-i18next';

import MainLayout from "../components/layout/Main"
import api from "../api"


function AddUser() {
  const [username, setUsername] = useState("")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showSuccess, setShowSuccess] = useState(false)
  const [showFailure, setShowFailure] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [loading, setLoading] = useState(false)

  const { t } = useTranslation()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setShowSuccess(false)
    setShowFailure(false)
    setErrorMessage("")
    
    // Basic validation
    if (!username.trim()) {
      setErrorMessage("Username is required")
      setShowFailure(true)
      return
    }
    
    try {
      setLoading(true)
      const userData = {
        username: username.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim()
      }
      
      const res = await api.post('/api/user/add/', userData)
      setPassword(res.data.data.password)
      setShowSuccess(true)
      
      // Clear form after successful creation
      setUsername("")
      setFirstName("")
      setLastName("")
      setEmail("")
    } catch (err) {
      setShowFailure(true)
      if (err.response && err.response.data && err.response.data.data) {
        // Format error messages from backend
        const errors = err.response.data.data
        const errorMessages = Object.entries(errors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join('\n')
        setErrorMessage(errorMessages)
      } else {
        setErrorMessage(t("add_user.error_alert_text"))
      }
    }
    setLoading(false)
  }

  useEffect(() => {
    document.title = t("add_user.page_title")
  }, [t])
  

  return (
    <MainLayout activeComponent='/users'>
      <Box sx={{ maxWidth: 800, margin: '0 auto' }}>
        {showSuccess && (
          <Alert 
            variant="filled" 
            severity="success" 
            sx={{ marginBottom: 3, borderRadius: '12px' }}
            onClose={() => setShowSuccess(false)}
          >
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {t("add_user.success_alert_text_1")}: {username}
            </Typography>
            <Typography variant="body2">
              {t("add_user.success_alert_text_2")}: <strong>{password}</strong>
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.9 }}>
              {t("add_user.password_note") || "Make sure to save this password - it won't be shown again!"}
            </Typography>
          </Alert>
        )}
        
        {showFailure && (
          <Alert 
            variant="filled" 
            severity="error" 
            sx={{ marginBottom: 3, borderRadius: '12px' }}
            onClose={() => setShowFailure(false)}
          >
            {errorMessage || t("add_user.error_alert_text")}
          </Alert>
        )}
        
        <Card variant="outlined" sx={{ borderRadius: '16px', boxShadow: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <PersonAddIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                {t("add_user.page_title")}
              </Typography>
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    required 
                    label={t("add_user.username_label")}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    helperText={t("add_user.username_helper") || "Username for login"}
                    disabled={loading}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label={t("add_user.first_name_label") || t("add_user.name_label")}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    helperText={t("add_user.first_name_helper") || "User's first name"}
                    disabled={loading}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label={t("add_user.last_name_label")}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    helperText={t("add_user.last_name_helper") || "User's last name"}
                    disabled={loading}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    type="email"
                    label={t("add_user.email_label") || "Email"}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    helperText={t("add_user.email_helper") || "User's email address"}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                <Button 
                  type="submit"
                  variant='contained' 
                  size="large"
                  disabled={loading}
                  sx={{ minWidth: 150 }}
                >
                  { loading ? (
                    <CircularProgress color="inherit" size={25} />
                  ) : (
                    <>
                      <PersonAddIcon sx={{ mr: 1 }} />
                      {t("add_user.add_button")}
                    </>
                  )}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    </MainLayout>
  )
}

export default AddUser